<script setup>
import { useAnalysisDataStore } from '@/store/modules/analysisDataStore';
import IconClose02Current from '@/components/svg/IconClose02Current.vue';

const analysisDataStore = useAnalysisDataStore();

// props 정의
const props = defineProps({
  guestUserId: {
    type: Number,
    default: 0,
  },
});

// 선택된 아이템을 추적하는 reactive 변수
const selectedItem = ref(null);
const showDetailLayer = ref(false);
const layerPosition = ref({ x: 0, y: 0 });
const layerStyle = ref('');

// 직업추천 데이터를 분리한 computed 속성들
const priorityPetFields = computed(() => analysisDataStore.pData?.반려친구요약 || {});
const generalPetFields = computed(
  () => analysisDataStore.pData?.반려친구추천?.우선추천?.pets || {},
);
const generalPlantFields = computed(
  () => analysisDataStore.pData?.반려친구추천?.우선추천?.plants || {},
);
const generalPetFieldsNormal = computed(
  () => analysisDataStore.pData?.반려친구추천?.일반추천?.pets || {},
);
const generalPlantFieldsNormal = computed(
  () => analysisDataStore.pData?.반려친구추천?.일반추천?.plants || {},
);

// 클릭 핸들러 함수
const clickPetItem = (item, event, style = '', position = 'bottom') => {
  selectedItem.value = item;
  showDetailLayer.value = true;
  layerStyle.value = style;

  // 클릭한 요소의 위치를 계산
  const rect = event.target.getBoundingClientRect();
  const layerWidth = 320; // 레이어의 고정 너비
  const layerHeight = 200; // 레이어의 예상 높이 (대략적인 값)
  const padding = 16; // 여백

  // 화면 크기 가져오기
  const windowWidth = window.innerWidth;
  const windowHeight = window.innerHeight;

  // X 위치 계산 (오른쪽으로 넘어가지 않도록)
  let x = rect.right - layerWidth;
  if (x < padding) {
    x = padding;
  } else if (x + layerWidth > windowWidth - padding) {
    x = windowWidth - layerWidth - padding;
  }

  // Y 위치 계산
  let y;
  if (position === 'top') {
    // 무조건 위쪽으로 표시 (단어 바로 위에서 시작)
    y = rect.top - layerHeight;
  } else {
    // 기본적으로 아래쪽, 공간이 부족하면 위쪽으로
    y = rect.bottom + 8;
    if (y + layerHeight > windowHeight - padding) {
      y = rect.top - layerHeight;
    }
  }

  layerPosition.value = { x, y };
};

// 레이어 닫기 함수
const closeDetailLayer = () => {
  showDetailLayer.value = false;
  selectedItem.value = null;
  layerStyle.value = '';
};
</script>
<template>
  <div class="pet-recommendation-wrap">
    <div class="round-tit-box st-personality-pet">나의 에너지를 채워줄 작은 친구들</div>
    <div class="pet-recommendation-section st-pink">
      <div class="pet-top-box">
        <div class="img-box"></div>
        <div class="desc-box">
          <div class="desc-tit">성향과 잘맞는 <br />조화로운 친구들이에요!</div>
          <div class="desc-txt">친구들을 클릭하면 정보가 나와요!</div>
        </div>
      </div>
      <div class="pet-list-box">
        <div class="item-box">
          <div class="tit-box">🐶 동물</div>
          <div class="pet-item">
            <div
              class="pet-name"
              @click="clickPetItem(item, $event, 'st-pink')"
              v-for="item in generalPetFields"
              :key="item.id"
            >
              {{ item.이름 }}
            </div>
          </div>
        </div>
        <div class="item-box">
          <div class="tit-box">🪴 식물</div>
          <div class="pet-item">
            <div
              class="pet-name"
              @click="clickPetItem(item, $event, 'st-pink')"
              v-for="item in generalPlantFields"
              :key="item.id"
            >
              {{ item.이름 }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="pet-recommendation-section">
      <div class="pet-top-box">
        <div class="img-box"></div>
        <div class="desc-box">
          <div class="desc-tit">
            함께 어울릴 수 있는 <br />
            자연의 친구들이에요!
          </div>
          <div class="desc-txt">친구들을 클릭하면 정보가 나와요!</div>
        </div>
      </div>
      <div class="pet-list-box">
        <div class="item-box">
          <div class="tit-box">🐶 동물</div>
          <div class="pet-item st-gray">
            <div
              class="pet-name"
              @click="clickPetItem(item, $event, '', 'top')"
              v-for="item in generalPetFieldsNormal"
              :key="item.id"
            >
              {{ item.이름 }}
            </div>
          </div>
        </div>
        <div class="item-box">
          <div class="tit-box">🪴 식물</div>
          <div class="pet-item st-gray">
            <div
              class="pet-name"
              @click="clickPetItem(item, $event, '', 'top')"
              v-for="item in generalPlantFieldsNormal"
              :key="item.id"
            >
              {{ item.이름 }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="pet-recommendation-desc">
    <div class="txt">
      이 분석이 당신을 이해하는 첫 걸음이 되어 스스로를 더 잘 이해하고 바라볼 수 있는 계기가 되기를
      바랍니다.
    </div>
    <div class="txt">
      사랑하는 사람들과의 관계를 조금 더 따뜻하게 마주하는 데에도 작은 도움이 되었기를 진심으로
      바랍니다.
    </div>
    <div class="txt b">당신을 빛나게 하는 에너지를 언제나 응원합니다.</div>
  </div>

  <!-- 상세설명 레이어 -->
  <div
    v-if="showDetailLayer"
    class="detail-layer"
    :class="layerStyle"
    :style="{ left: layerPosition.x + 'px', top: layerPosition.y + 'px' }"
  >
    <div class="detail-content">
      <div class="detail-title">
        <span>{{ selectedItem?.이름 }}</span>
        <button class="btn-close" @click="closeDetailLayer">
          <IconClose02Current />
        </button>
      </div>
      <div class="detail-info">
        <div
          class="info-item"
          v-for="(value, key) in selectedItem"
          :key="key"
          v-if="key !== '이름'"
        >
          <span class="info-label">{{ key }}</span>
          <span class="info-value">{{ value }}</span>
        </div>
      </div>
    </div>
  </div>

  <div class="gap-large"></div>
</template>

<style lang="scss">
@import '@/assets/nangman_style/_analysis_main_color.scss';
.round-tit-box.st-personality-pet {
  margin-bottom: 0 !important;
}
.pet-recommendation-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
}

.section-title {
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 1rem;
  color: #333;
}
.pet-recommendation-section {
  display: flex;
  padding: 16px;
  flex-direction: column;
  align-items: center;
  align-self: stretch;
  border-radius: 12px;
  background: var(--Gray-200, #f8f8f8);

  .pet-top-box {
    display: flex;
    padding: 0px 20px;
    align-items: center;
    gap: 16px;
    align-self: stretch;
    .img-box {
      width: 100px;
      height: 100px;
      aspect-ratio: 1/1;
      background: url('@manseImages/pet_black.png') no-repeat center center;
      background-size: 100% auto;
    }
    .desc-box {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      gap: 2px;
      flex: 1 0 0;
      .desc-tit {
        color: var(--Gray-800, #333);
        font-family: Pretendard;
        font-size: 15px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px; /* 160% */
      }
      .desc-txt {
        color: var(--Gray-600, #757575);
        font-family: Pretendard;
        font-size: 13px;
        font-style: normal;
        font-weight: 400;
        line-height: 20px; /* 153.846% */
      }
    }
  }
  &.st-pink {
    border-radius: 12px;
    border: 1px solid $analysis-personality-border;
    background: $analysis-personality-light;
    .pet-top-box {
      .img-box {
        background: url('@manseImages/pet_pink.png') no-repeat center center;
        background-size: 100% auto;
      }
    }
  }
  .pet-list-box {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
    width: 100%;
    .item-box {
      display: flex;
      padding: 16px;
      justify-content: space-between;
      align-items: flex-start;
      align-self: stretch;
      gap: 16px;
      border-radius: 12px;
      background: var(--gray-100-white, #fff);
      width: 100%;
      .tit-box {
        color: var(--Gray-800, #333);
        text-align: center;
        font-family: Pretendard;
        font-size: 14px;
        font-style: normal;
        font-weight: 700;
        line-height: 24px; /* 171.429% */
        flex-shrink: 0;
      }
      .pet-item {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 8px;
        cursor: pointer;
        &.st-gray {
          .pet-name {
            background: rgba(117, 117, 117, 0.1);
            color: var(--Gray-600, #757575);
          }
        }
        .pet-name {
          display: flex;
          padding: 3px 6px;
          justify-content: center;
          align-items: flex-start;
          gap: 10px;
          border-radius: 4px;
          white-space: nowrap;
          flex-shrink: 0;
          background: rgba(243, 174, 17, 0.1);
          color: $analysis-personality;
          font-family: Pretendard;
          font-size: 13px;
          font-style: normal;
          font-weight: 700;
          line-height: 18px; /* 138.462% */
        }
      }
    }
  }
}
.pet-recommendation-desc {
  padding-top: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
  color: var(--Gray-800, #333);
  font-family: Pretendard;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 160% */
  .txt {
    &.b {
      color: $analysis-personality;
      font-family: Pretendard;
      font-size: 15px;
      font-style: normal;
      font-weight: 700;
      line-height: 24px; /* 160% */
    }
  }
}

// 상세설명 레이어 스타일
.detail-layer {
  position: fixed;
  display: flex;
  width: 320px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 12px;
  border: 1px solid var(--Gray-300, #ebebeb);
  background: var(--gray-100-white, #fff);
  box-shadow: 0px 0px 20px 0px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  &.st-pink {
    .detail-title {
      background: rgba(243, 174, 17, 0.1);
      color: $analysis-personality;
    }
    .info-label {
      background: $analysis-personality;
    }
  }
}

.detail-content {
  margin-bottom: 16px;
  padding: 0;
}

.detail-info {
  display: flex;
  padding: 12px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
  align-self: stretch;
}

.info-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
}

.info-label {
  color: var(--gray-100-white, #fff);
  font-family: Pretendard;
  font-size: 13px;
  font-style: normal;
  font-weight: 700;
  line-height: 22px; /* 169.231% */
  border-radius: 60px;
  background: var(--Gray-600, #757575);
  display: flex;
  height: 22px;
  padding: 0px 6px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
  min-width: fit-content;
}

.info-value {
  color: var(--Gray-800, #333);
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 22px; /* 157.143% */
}

.detail-title {
  display: flex;
  padding: 12px;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  align-self: stretch;
  background: rgba(117, 117, 117, 0.1);
  color: var(--Gray-600, #757575);
  font-family: Pretendard;
  font-size: 15px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 160% */
}

.detail-description {
  font-family: Pretendard;
  font-size: 13px;
  font-weight: 400;
  color: #333;
  line-height: 18px;
}

.detail-layer {
  .btn-close {
    width: 20px;
    height: 20px;
    aspect-ratio: 1/1;
    cursor: pointer;
    color: #333;
    transition: background-color 0.2s;
  }
}
</style>
